/* ==========================================================================
   HerbHue premium header — vanilla JS, no dependencies.

   Handles:
     - Announcement bar rotation (auto, pause on hover, arrow nav, reduced-motion)
     - Sticky-scroll shadow toggle (adds .is-scrolled after scrolling past 8px)
     - Desktop nav dropdowns (hover + keyboard, Escape closes, click-outside closes)
     - Slide-down search overlay (open/close, focus input, Esc to close, focus trap)
     - Mobile drawer (open/close, focus trap, Esc to close, body scroll lock)
     - Cart count live-update by listening for Shopify cart change events

   Idempotent: safe to include multiple times or on section re-render.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__hhHeaderInit) return;
  window.__hhHeaderInit = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Utility: focus trap ---------- */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    var focusables = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE))
      .filter(function (el) { return el.offsetParent !== null; });
    if (!focusables.length) return;
    var first = focusables[0];
    var last  = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- Body scroll lock ---------- */
  var scrollY = 0;
  function lockScroll() {
    scrollY = window.scrollY || document.documentElement.scrollTop;
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.classList.add('hh-scroll-locked');
  }
  function unlockScroll() {
    document.body.classList.remove('hh-scroll-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  /* ==========================================================================
     ANNOUNCEMENT BAR
     ========================================================================== */
  function initAnnouncement() {
    var bar = document.querySelector('[data-hh-announcement]');
    if (!bar) return;
    var slides = bar.querySelectorAll('[data-hh-announcement-slide]');
    if (slides.length < 2) return;

    var speed = parseInt(bar.getAttribute('data-hh-speed'), 10) || 4;
    var idx = 0;
    var timer = null;
    var paused = false;

    function show(i) {
      slides.forEach(function (s, k) {
        var active = k === i;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      idx = i;
    }
    function next() { show((idx + 1) % slides.length); }
    function prev() { show((idx - 1 + slides.length) % slides.length); }

    function start() {
      if (reduceMotion || paused) return;
      stop();
      timer = window.setInterval(next, speed * 1000);
    }
    function stop() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    bar.addEventListener('mouseenter', function () { paused = true; stop(); });
    bar.addEventListener('mouseleave', function () { paused = false; start(); });
    bar.addEventListener('focusin',    function () { paused = true; stop(); });
    bar.addEventListener('focusout',   function () { paused = false; start(); });

    var prevBtn = bar.querySelector('[data-hh-announcement-prev]');
    var nextBtn = bar.querySelector('[data-hh-announcement-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { prev(); start(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { next(); start(); });

    // Swipe on touch devices
    var touchStartX = null;
    bar.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    bar.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 40) { dx < 0 ? next() : prev(); start(); }
      touchStartX = null;
    }, { passive: true });

    start();
  }

  /* ==========================================================================
     STICKY SCROLL SHADOW
     ========================================================================== */
  function initStickyShadow() {
    var header = document.querySelector('[data-hh-header]');
    if (!header) return;
    var threshold = 8;
    var ticking = false;

    function apply() {
      var y = window.scrollY || document.documentElement.scrollTop;
      header.classList.toggle('is-scrolled', y > threshold);
      ticking = false;
    }
    apply();
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(apply);
        ticking = true;
      }
    }, { passive: true });
  }

  /* ==========================================================================
     DESKTOP NAV DROPDOWNS
     ========================================================================== */
  function initNavDropdowns() {
    var items = document.querySelectorAll('[data-hh-nav-item].has-submenu');
    if (!items.length) return;

    function closeAll(exceptItem) {
      items.forEach(function (it) {
        if (it === exceptItem) return;
        it.setAttribute('data-hh-open', 'false');
        var t = it.querySelector('[data-hh-submenu-toggle]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }
    function open(it) {
      it.setAttribute('data-hh-open', 'true');
      var t = it.querySelector('[data-hh-submenu-toggle]');
      if (t) t.setAttribute('aria-expanded', 'true');
    }
    function close(it) {
      it.setAttribute('data-hh-open', 'false');
      var t = it.querySelector('[data-hh-submenu-toggle]');
      if (t) t.setAttribute('aria-expanded', 'false');
    }

    items.forEach(function (it) {
      var toggle = it.querySelector('[data-hh-submenu-toggle]');

      // Hover (desktop)
      it.addEventListener('mouseenter', function () { closeAll(it); open(it); });
      it.addEventListener('mouseleave', function () { close(it); });

      // Keyboard: Enter/Space toggles, Escape closes
      if (toggle) {
        toggle.addEventListener('click', function (e) {
          // If pointer click on a real link, follow the link — only intercept when it's the caret area or keyboard
          // Behavior: on desktop we let click follow to parent link; toggling happens via hover/keyboard.
          // But if the parent link is anchor with a real URL, we don't want click to also toggle.
          // For touch devices (no hover), we intercept the first click to open the submenu.
          if (window.matchMedia('(hover: none)').matches) {
            if (it.getAttribute('data-hh-open') !== 'true') {
              e.preventDefault();
              closeAll(it);
              open(it);
            }
          }
        });
        toggle.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (it.getAttribute('data-hh-open') === 'true') { close(it); }
            else { closeAll(it); open(it); }
          } else if (e.key === 'Escape') {
            close(it);
            toggle.focus();
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            closeAll(it); open(it);
            var firstLink = it.querySelector('.hh-header__submenu a');
            if (firstLink) firstLink.focus();
          }
        });
      }

      // Escape inside the submenu returns to the toggle
      var submenu = it.querySelector('[data-hh-submenu]');
      if (submenu) {
        submenu.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') {
            close(it);
            if (toggle) toggle.focus();
          }
        });
      }
    });

    // Click outside closes all
    document.addEventListener('click', function (e) {
      var clickedInside = false;
      items.forEach(function (it) { if (it.contains(e.target)) clickedInside = true; });
      if (!clickedInside) closeAll();
    });
  }

  /* ==========================================================================
     SEARCH OVERLAY
     ========================================================================== */
  function initSearch() {
    var search = document.querySelector('[data-hh-search]');
    if (!search) return;
    var openers = document.querySelectorAll('[data-hh-search-open]');
    var closers = search.querySelectorAll('[data-hh-search-close]');
    var input = search.querySelector('[data-hh-search-input]');

    var isOpen = false;
    function openSearch() {
      isOpen = true;
      search.setAttribute('data-hh-search-open', 'true');
      search.setAttribute('aria-hidden', 'false');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
      // Focus input after transition starts
      window.setTimeout(function () { if (input) input.focus(); }, 60);
    }
    function closeSearch() {
      isOpen = false;
      search.setAttribute('data-hh-search-open', 'false');
      search.setAttribute('aria-hidden', 'true');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
      var lastOpener = openers[0];
      if (lastOpener) lastOpener.focus();
    }

    openers.forEach(function (o) {
      o.addEventListener('click', function () {
        if (isOpen) closeSearch();
        else openSearch();
      });
    });
    closers.forEach(function (c) { c.addEventListener('click', closeSearch); });

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); closeSearch(); }
      if (e.key === 'Tab') { trapFocus(search.querySelector('.hh-search__panel'), e); }
    });
  }

  /* ==========================================================================
     MOBILE DRAWER
     ========================================================================== */
  function initDrawer() {
    var drawer = document.querySelector('[data-hh-drawer]');
    if (!drawer) return;
    var openers = document.querySelectorAll('[data-hh-drawer-open]');
    var closers = drawer.querySelectorAll('[data-hh-drawer-close]');

    var isOpen = false;

    function openDrawer() {
      isOpen = true;
      drawer.setAttribute('data-hh-drawer-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
      lockScroll();
      // Focus close button so keyboard can Esc/Tab out
      window.setTimeout(function () {
        var closeBtn = drawer.querySelector('.hh-drawer__close');
        if (closeBtn) closeBtn.focus();
      }, 100);
    }
    function closeDrawer() {
      isOpen = false;
      drawer.setAttribute('data-hh-drawer-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
      unlockScroll();
      // Return focus to the opener
      var opener = openers[0];
      if (opener) opener.focus();
    }

    openers.forEach(function (o) { o.addEventListener('click', openDrawer); });
    closers.forEach(function (c) { c.addEventListener('click', closeDrawer); });

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); }
      if (e.key === 'Tab') { trapFocus(drawer.querySelector('.hh-drawer__panel'), e); }
    });

    // Close drawer when a nav link is clicked (so page navigates without stale UI)
    drawer.querySelectorAll('a[href]').forEach(function (a) {
      a.addEventListener('click', function () { closeDrawer(); });
    });
  }

  /* ==========================================================================
     CART COUNT LIVE UPDATE
     Listens for common Shopify cart update signals from ajax carts/themes.
     Falls back gracefully if no ajax cart is in use.
     ========================================================================== */
  function initCartCount() {
    var badges = document.querySelectorAll('[data-hh-cart-count]');
    if (!badges.length) return;

    function setCount(count) {
      var n = parseInt(count, 10);
      if (isNaN(n) || n < 0) n = 0;
      badges.forEach(function (b) {
        b.textContent = String(n);
        b.classList.toggle('is-empty', n === 0);
        b.setAttribute('aria-hidden', n === 0 ? 'true' : 'false');
        // Bump animation
        b.classList.remove('is-bumped');
        // Force reflow so the animation replays
        void b.offsetWidth;
        b.classList.add('is-bumped');
      });
      // Update aria-label on cart link
      var cartLinks = document.querySelectorAll('[data-hh-cart]');
      cartLinks.forEach(function (l) {
        l.setAttribute('aria-label', 'Cart, ' + n + ' item' + (n !== 1 ? 's' : ''));
      });
    }

    function fetchCart() {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) { if (data) setCount(data.item_count); })
        .catch(function () {});
    }

    // Custom event fired by many themes and by our own bundle
    document.addEventListener('cart:updated', function (e) {
      if (e && e.detail && typeof e.detail.item_count !== 'undefined') {
        setCount(e.detail.item_count);
      } else {
        fetchCart();
      }
    });

    // Shopify's ajax cart standard event (some themes)
    document.addEventListener('cart:refresh', fetchCart);
    document.addEventListener('theme:cart:updated', fetchCart);

    // Intercept forms that add to cart so we can refresh count after
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (form.getAttribute('action') && form.getAttribute('action').indexOf('/cart/add') !== -1) {
        // Give the theme a moment to process, then refresh
        window.setTimeout(fetchCart, 500);
      }
    }, true);
  }

  /* ==========================================================================
     BOOT
     ========================================================================== */
  function init() {
    initAnnouncement();
    initStickyShadow();
    initNavDropdowns();
    initSearch();
    initDrawer();
    initCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init pieces when Shopify theme editor re-renders sections
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target) return;
    if (e.target.classList.contains('hh-announcement-section')) initAnnouncement();
    if (e.target.classList.contains('hh-header-section')) {
      initStickyShadow();
      initNavDropdowns();
      initSearch();
      initDrawer();
      initCartCount();
    }
  });
})();